<script setup lang="ts">

import { computed, markRaw, onBeforeUnmount, ref, watch, inject } from 'vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import ScheduleBadgePair from '@/components/schedules/ScheduleBadgePair.vue';
import { COUNT_BUTTON_CLASS } from '@/components/schedules/countButton';
import { getArrayColumnEntry, type ArrayColumnBadge } from '@/components/arrayColumns';
import DefaultArrayContent from '@/components/arrayColumns/DefaultArrayContent.vue';
import { useInspectorStore } from '@/stores/inspectorStore';
import { INSPECTOR_HOST } from '@/composables/inspectorHostContext';
import { arrayItemName } from '@/components/arrayColumns/arrayItemName';
import RecordLink from '@/components/RecordLink.vue';
import { useLazyTooltip } from '@/composables/useLazyTooltip';

const props = defineProps<{
  field: string;
  dataType: string;
  data: any;
}>();

const inspectorStore = useInspectorStore();

// Extract class type from dataType (e.g., "BucketGenre[]" -> "BucketGenre")
const classType = computed(() => {
  if (props.dataType.endsWith('[]')) {
    return props.dataType.slice(0, -2);
  }
  return '';
});

// Look up registry entry for this class type
const registryEntry = computed(() => {
  return classType.value ? getArrayColumnEntry(classType.value) : null;
});

// Get the children array data
// Field pattern: 'albums.display_string' where 'albums' is the array field
const childrenArray = computed(() => {
  const parts = props.field.split('.');
  if (parts.length !== 2 || parts[1] !== 'display_string') {
    return [];
  }
  const arrayField = parts[0];
  const arrayData = props.data[arrayField];
  const raw = Array.isArray(arrayData) ? arrayData : [];
  const transform = registryEntry.value?.items;
  return transform ? transform(raw, props.data ?? {}) : raw;
});

// Get display strings from children array. A registry entry may compute them from the whole
// array and the parent row instead (the custom-list Schedules column does, to summarise each
// schedule and put the list's own zone first).
const childrenDisplayStrings = computed(() => {
  if (!childrenArray.value || childrenArray.value.length === 0) {
    return [];
  }
  const itemSummaries = registryEntry.value?.itemSummaries;
  if (itemSummaries) {
    return itemSummaries(childrenArray.value, props.data ?? {}).filter((str: string) => str !== '');
  }
  return childrenArray.value
    .map((item: unknown) => arrayItemName(item, classType.value))
    .filter((str: string) => str !== '');
});

// Items drawn as links, for a registry entry that opts in (linksItems). Each needs an id to open.
// The names are the links, so the cell draws them in place of the joined text and its tooltip.
// (No comment in the template: the registry parity snapshots pin the other columns' HTML.)
const linkedItems = computed(() => {
  if (!registryEntry.value?.linksItems) {
    return null;
  }
  return childrenArray.value
    .map((item: unknown) => {
      const id = item && typeof item === 'object' ? (item as Record<string, unknown>).id : null;
      const name = arrayItemName(item, classType.value);
      return (typeof id === 'number' || typeof id === 'string') && name !== '' ? { id, name } : null;
    })
    .filter((linkedItem): linkedItem is { id: number | string; name: string } => linkedItem !== null);
});

// Join display strings for inline display
const joinedDisplayStrings = computed(() => {
  return childrenDisplayStrings.value.join(', ');
});

const hasItems = computed(() => childrenArray.value.length > 0);

// Opt-in split count (registry `badges`): zero counts are hidden unless the badge sets
// alwaysShow. Null when the entry does not opt in, or when nothing is left to draw, so the cell
// falls back to the single count badge.
const splitBadges = computed<ArrayColumnBadge[] | null>(() => {
  const badges = registryEntry.value?.badges;
  if (!badges || !hasItems.value) {
    return null;
  }
  const visible = badges(childrenArray.value, props.data ?? {}).filter((badge) => badge.count > 0 || badge.alwaysShow);
  return visible.length > 0 ? visible : null;
});

// The text beside the count: the joined display strings, or the registry's own
// inline component (the Schedules one-line cards), which lays itself out in a row.
const inlineTextClass = computed(() =>
  registryEntry.value?.inline
    ? 'flex min-w-0 items-center overflow-hidden py-1 px-1 -mx-1'
    : 'truncate min-w-0 py-1 px-1 -mx-1',
);

// Badge label: count when items exist, or emptyLabel from registry (e.g. "Add"), or nothing
const badgeLabel = computed(() => {
  if (hasItems.value) {
    return String(childrenArray.value.length);
  }
  return registryEntry.value?.emptyLabel || null;
});

// Lazy tooltip: render a plain truncated span and only mount the reka-ui Tooltip on first
// hover or keyboard focus. A results table can have an array column in every row, so eagerly
// mounting a tooltip per array cell adds up to the load-time freeze. The list tooltip always shows
// on hover (it's an at-a-glance expansion, not overflow-gated), so there is no overflow check.
// The swap goes through useLazyTooltip, the same path LazyTooltip and OverflowTooltip use: it
// waits the normal open delay, and it never opens once the pointer has left the cell.
const tooltipOpen = ref(false);
// Template ref on the trigger <span>. The lazy path uses it to tell whether the pointer is still
// on the trigger, and the re-render close net below asks whether it is still `:hover`.
const triggerEl = ref<HTMLElement | null>(null);
function closeTooltip(): void {
  tooltipOpen.value = false;
}
const {
  activated: tooltipActivated,
  onProbePointerOver,
  onProbePointerOut,
  onProbeFocusIn,
  onProbeFocusOut,
} = useLazyTooltip({
  open: () => {
    tooltipOpen.value = true;
  },
  close: closeTooltip,
  triggerElement: () => triggerEl.value,
});
// The Tooltip's portal lives at body level and can outlive the cell, so close it on unmount.
onBeforeUnmount(closeTooltip);

// SAU-186 re-render close net (mirrors useOverflowTooltip's watch(displayDeps)): when the row's
// data re-derives (track-matching expand/collapse) while the tooltip is open and the pointer is no
// longer over the trigger, reka will never emit a `pointerleave` for the swapped-out trigger
// element, so the body-level portal would linger. Watch the same reactive value the cell renders
// from (`childrenDisplayStrings` — both the trigger text and the tooltip body map over it) and
// force-close when it changes with a stale hover.
watch(childrenDisplayStrings, () => {
  if (!tooltipOpen.value) {
    return;
  }
  const stillHovered =
    !!triggerEl.value &&
    typeof triggerEl.value.matches === 'function' &&
    triggerEl.value.matches(':hover');
  if (!stillHovered) {
    closeTooltip();
  }
});

const isInsideInspector = inject(INSPECTOR_HOST, false);

function openInspector() {
  const entry = registryEntry.value;
  const inspectorProps = {
    field: props.field,
    dataType: props.dataType,
    data: props.data,
    classType: classType.value,
  };

  // Inside the inspector this is a drill-down: push so the back chevron returns
  // to the record the user came from. From a table cell, open (replace) as before.
  const openView = isInsideInspector
    ? inspectorStore.push.bind(inspectorStore)
    : inspectorStore.open.bind(inspectorStore);
  if (entry) {
    openView(entry.title, markRaw(entry.component), inspectorProps);
  } else {
    openView(classType.value || 'Details', markRaw(DefaultArrayContent), inspectorProps);
  }
}

// The template's outer line carries `max-w-full`, which caps it at its field's
// width so the names truncate. Without it an inline-flex box grows to fit every
// name, and a long owners list made a phone's record page pan sideways.
</script>

<template>
  <span v-if="badgeLabel" class="inline-flex max-w-full items-center gap-2 min-w-0">
    <!-- Badge button opens inspector -->
    <button
      v-if="hasItems && !splitBadges"
      data-testid="array-badge"
      type="button"
      class="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary flex-shrink-0 hover:bg-primary/20 transition-colors cursor-pointer"
      @click="openInspector"
    >
      {{ badgeLabel }}
    </button>
    <!-- Split counts: the same joined control, and the same one tooltip listing
         every count, as the custom-list search card. One button opens the view. -->
    <button
      v-else-if="splitBadges"
      data-testid="array-badge"
      type="button"
      :class="COUNT_BUTTON_CLASS"
      @click="openInspector"
    >
      <ScheduleBadgePair :badges="splitBadges" layout="joined" />
    </button>
    <button
      v-else
      data-testid="array-badge"
      type="button"
      class="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-medium border border-dashed border-muted-foreground/50 text-muted-foreground flex-shrink-0 hover:border-muted-foreground hover:text-foreground transition-colors cursor-pointer"
      @click="openInspector"
    >
      {{ badgeLabel }}
    </button>
    <!-- Tooltip showing all display strings — lazily mounted on first hover (see useLazyTooltip) -->
    <template v-if="hasItems">
      <span v-if="linkedItems && linkedItems.length > 0" class="truncate min-w-0 py-1 px-1 -mx-1" data-testid="array-item-links">
        <template v-for="(linkedItem, index) in linkedItems" :key="linkedItem.id">
          <template v-if="index > 0">, </template>
          <RecordLink :entity-id="classType" :record-id="linkedItem.id" :label="linkedItem.name" inline>{{ linkedItem.name }}</RecordLink>
        </template>
      </span>
      <span
        v-else-if="!tooltipActivated"
        :class="inlineTextClass"
        tabindex="-1"
        @pointerover="onProbePointerOver"
        @pointerout="onProbePointerOut"
        @focusin="onProbeFocusIn"
        @focusout="onProbeFocusOut"
      >
        <component :is="registryEntry.inline" v-if="registryEntry?.inline" :row="data ?? {}" :with-title="false" />
        <template v-else>{{ joinedDisplayStrings }}</template>
      </span>
      <Tooltip v-else :open="tooltipOpen" @update:open="(value) => (tooltipOpen = value)">
        <TooltipTrigger as-child>
          <span ref="triggerEl" :class="inlineTextClass" @pointerleave="closeTooltip">
            <component :is="registryEntry.inline" v-if="registryEntry?.inline" :row="data ?? {}" :with-title="false" />
            <template v-else>{{ joinedDisplayStrings }}</template>
          </span>
        </TooltipTrigger>
        <!-- Body is purely display text (the list of child display strings, no links/buttons), so
             pointer-events-none keeps it non-interactive — consistent with OverflowTooltip and
             avoiding open/close churn as the pointer moves toward the content. -->
        <TooltipContent
          class="max-w-[200px] break-words pointer-events-none"
          :collision-padding="16"
          side="top"
          align="start"
        >
          <div class="flex flex-col gap-1">
            <div v-for="(displayString, index) in childrenDisplayStrings" :key="index" class="break-words">
              {{ displayString }}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </template>
  </span>
  <span v-else class="text-muted-foreground text-sm">—</span>
</template>
